import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, Trash2, Save, Loader2, Pencil, X, ListPlus } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "text",     l: "Testo" },
  { v: "number",   l: "Numero" },
  { v: "date",     l: "Data" },
  { v: "select",   l: "Lista (select)" },
  { v: "textarea", l: "Testo lungo" },
  { v: "boolean",  l: "Sì / No" },
];

const empty = () => ({ key: "", label: "", type: "text", options: [], showInList: false, showInForm: true, sort: 0 });

export default function FieldManagerPage() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/fields"); setItems(r.data.items || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (f, isNew) => {
    if (!f.label?.trim() || !f.key?.trim()) { toast.error("Chiave ed etichetta obbligatorie"); return; }
    try {
      const payload = { ...f, options: f.type === "select" ? (f.options || []).filter(Boolean) : [] };
      if (isNew) await api.post("/fields", payload);
      else await api.patch(`/fields/${f.id}`, payload);
      toast.success("Campo salvato");
      setCreating(null); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il campo? I dati esistenti restano nei clienti ma il campo non sarà più mostrato.")) return;
    await api.delete(`/fields/${id}`);
    toast.success("Eliminato"); load();
  };

  return (
    <AppLayout
      title="Campi Personalizzati"
      subtitle="Estendi l'anagrafica cliente"
      actions={
        <button onClick={() => setCreating(empty())} data-testid="btn-new-field" className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} ><Plus className="w-4 h-4" /> Nuovo campo</button>
      }
    >
      <div className="bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] p-5">
        <p className="text-sm text-slate-600 mb-4">
          Aggiungi campi personalizzati (es. <em>Reddito mensile</em>, <em>Tipologia contratto</em>) all'anagrafica cliente. I campi vengono mostrati nella scheda cliente e, se attivi, anche nella lista clienti.
        </p>

        {creating && <FieldEditor f={creating} onChange={setCreating} onSave={() => save(creating, true)} onCancel={() => setCreating(null)} />}

        {loading ? <div className="py-10 text-center inline text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          : items.length === 0 && !creating ? (
            <div className="py-10 text-center text-sm text-slate-500 mx-auto text-slate-300 mb-2"><ListPlus className="w-10 h-10" /> Nessun campo personalizzato.</div>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.1em] text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="text-left font-semibold py-2.5 px-3">Etichetta</th>
                    <th className="text-left font-semibold py-2.5 px-3">Chiave</th>
                    <th className="text-left font-semibold py-2.5 px-3">Tipo</th>
                    <th className="text-center font-semibold py-2.5 px-3">In lista</th>
                    <th className="text-center font-semibold py-2.5 px-3">In form</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => editing?.id === f.id
                    ? <tr key={f.id}><td colSpan={6} className="p-2 bg-amber-50/30"><FieldEditor f={editing} onChange={setEditing} onSave={() => save(editing, false)} onCancel={() => setEditing(null)} /></td></tr>
                    : (
                      <tr key={f.id} className="border-b border-[#F0F2F5] tbl-row" data-testid={`field-${f.id}`}>
                        <td className="py-2.5 px-3 font-medium text-slate-900">{f.label}</td>
                        <td className="py-2.5 px-3 font-mono text-xs text-slate-600">{f.key}</td>
                        <td className="py-2.5 px-3 text-slate-700">{TYPES.find((t) => t.v === f.type)?.l || f.type}</td>
                        <td className="py-2.5 px-3 text-center">{f.showInList ? "Sì" : "—"}</td>
                        <td className="py-2.5 px-3 text-center">{f.showInForm ? "Sì" : "—"}</td>
                        <td className="py-2.5 px-3 flex items-center gap-1">
                          <button onClick={() => setEditing({ ...f })} className="p-1.5 hover:bg-slate-100 rounded-[3px] text-slate-600"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => del(f.id)} className="p-1.5 hover:bg-rose-50 rounded-[3px] text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </AppLayout>
  );
}

function FieldEditor({ f, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...f, [k]: v });
  return (
    <div className="border border-amber-300 bg-amber-50/40 rounded-[3px] p-4 mb-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Etichetta</label>
          <input value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="Es. Reddito mensile" data-testid="field-label" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Chiave (snake_case)</label>
          <input value={f.key} onChange={(e) => set("key", e.target.value.toLowerCase().replace(/\s+/g, "_"))} placeholder="reddito_mensile" data-testid="field-key" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] font-mono" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Tipo</label>
          <select value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="field-type" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
      </div>
      {f.type === "select" && (
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Opzioni (una per riga)</label>
          <textarea
            rows={3}
            value={(f.options || []).join("\n")}
            onChange={(e) => set("options", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
            className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] font-mono"
          />
        </div>
      )}
      <div className="mt-3 flex items-center gap-4 text-sm text-slate-700">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.showInForm} onChange={(e) => set("showInForm", e.target.checked)} /> Mostra nel form cliente</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.showInList} onChange={(e) => set("showInList", e.target.checked)} /> Mostra nella lista</label>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-slate-300 rounded-[3px] hover:bg-white text-slate-700 inline mr-1"><X className="w-4 h-4" /> Annulla</button>
        <button onClick={onSave} data-testid="field-save" className="px-3 py-1.5 text-sm text-white rounded-[3px] flex items-center gap-1.5" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} ><Save className="w-4 h-4" /> Salva</button>
      </div>
    </div>
  );
}
